import { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { CalendarDays, Wallet, LayoutDashboard, LogOut, WifiOff, RefreshCw, Route } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { can, hasFeature, ROLES } from "../../lib/labels";
import { onQueueChange, queueList, syncQueue } from "../../lib/offline";
import { LangSwitch } from "../../components/LangSwitch";

function SyncBar() {
  const [online, setOnline] = useState(navigator.onLine);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const refresh = () => queueList().then((l) => setCount(l.length)).catch(() => {});
  const sync = async () => {
    setBusy(true);
    const r = await syncQueue();
    setBusy(false);
    if (r.done) toast.success(`${r.done} élément(s) synchronisé(s)`);
    if (r.failed) toast.error(`${r.failed} élément(s) refusé(s) par le serveur`);
  };
  useEffect(() => {
    refresh();
    const off = onQueueChange(refresh);
    const up = () => { setOnline(true); sync(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    const timer = setInterval(() => { if (navigator.onLine) syncQueue(); }, 30000);
    syncQueue();
    return () => { off(); window.removeEventListener("online", up); window.removeEventListener("offline", down); clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (online && count === 0) return null;
  return (
    <div data-testid="nomade-sync-bar" className={`flex items-center justify-between gap-3 px-4 py-2 text-xs font-semibold ${online ? "bg-amber-50 text-amber-800" : "bg-slate-800 text-white"}`}>
      <span className="flex items-center gap-2">{!online && <WifiOff className="h-4 w-4" />}{online ? `${count} élément(s) en attente de synchronisation` : `Hors ligne — saisies enregistrées sur l'appareil${count ? ` (${count} en attente)` : ""}`}</span>
      {online && count > 0 && <button data-testid="nomade-sync-button" onClick={sync} disabled={busy} className="flex items-center gap-1 rounded bg-amber-200 px-2 py-1"><RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />Synchroniser</button>}
    </div>
  );
}

const Tab = ({ to, icon: Icon, label, id, end }) => (
  <NavLink to={to} end={end} data-testid={`nomade-tab-${id}`}
    className={({ isActive }) => `flex flex-1 flex-col items-center gap-1 py-2.5 text-xs font-semibold transition-colors ${isActive ? "text-[#1559d6]" : "text-slate-500"}`}>
    <Icon className="h-5 w-5" />{label}
  </NavLink>
);

export default function NomadeLayout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-lg">
        <div className="mx-auto flex h-14 max-w-2xl items-center justify-between px-4">
          <Link to="/nomade" className="flex items-center gap-2" data-testid="nomade-home-link">
            <img src="/logo-icon.png" alt="Logidem" className="h-7 w-auto" />
            <span className="font-display text-lg font-extrabold text-[#0b2c6b]">Nomade</span>
          </Link>
          <div className="flex items-center gap-3 text-right">
            <LangSwitch loggedIn />
            <div className="leading-tight">
              <p className="text-sm font-semibold text-slate-900" data-testid="nomade-user-name">{user?.name}</p>
              <p className="text-[11px] text-teal-600">{ROLES[user?.role]}</p>
            </div>
            <button onClick={logout} data-testid="nomade-logout-button" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
        <SyncBar />
      </header>
      <main className="mx-auto max-w-2xl px-4 pb-28 pt-5">
        {hasFeature(user, "field") ? <Outlet /> : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="nomade-locked">
            <p className="font-semibold text-slate-900">L'application Nomade est incluse à partir de la formule Pro.</p>
            <p className="mt-1 text-sm text-slate-500">{can(user, "billing") ? "Changez de formule dans Abonnement." : "Demandez au dirigeant de changer de formule."}</p>
            {can(user, "billing") && <Link to="/app/abonnement" className="mt-4 inline-block rounded-full bg-[#0b2c6b] px-4 py-2 text-sm font-semibold text-white" data-testid="nomade-upgrade-link">Voir les formules</Link>}
          </div>
        )}
      </main>
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-2xl">
          <Tab to="/nomade" end icon={CalendarDays} label="Planning" id="planning" />
          <Tab to="/nomade/itineraire" icon={Route} label="Itinéraire" id="itineraire" />
          <Tab to="/nomade/depenses" icon={Wallet} label="Dépenses" id="depenses" />
          {can(user, "dashboard") && <Tab to="/app" icon={LayoutDashboard} label="Bureau" id="bureau" />}
        </div>
      </nav>
    </div>
  );
}
